import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lang, Reservation, ServiceId } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { waLink, waBookingMessage } from '../lib/whatsapp';
import { Icon } from '../components/Icon';
import { TimeSelect } from '../components/TimeSelect';
import { Pill } from '../components/Pill';
import { Tags } from '../components/Tags';
import { useFocusTrap } from '../components/useFocusTrap';

interface ResvDrawerProps {
  resv: Reservation | null;
  lang: Lang;
  onClose: () => void;
}

/**
 * Hour anchored to the seeded demo day (12 June 2026) so edited times
 * still land alongside the rest of the fixture data in the calendar.
 * In production the date stepper would drive this.
 */
const DEMO_DAY = '2026-06-12';

export function ResvDrawer({ resv: snapshot, lang, onClose }: ResvDrawerProps) {
  const { customers, services, tables, reservations, tenant } = useDashboard();
  const actions = useDashboardActions();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<null | 'confirm' | 'cancel' | 'seat' | 'table' | 'save'>(null);
  const [editing, setEditing] = useState(false);
  // Local form state for edit mode. Seeded each time the drawer
  // opens or `editing` flips on.
  const [form, setForm] = useState<{
    name: string;
    phone: string;
    covers: number;
    start: string;
    service: ServiceId;
    noteIt: string;
  } | null>(null);

  // The drawer receives a *snapshot* of the reservation when it opened.
  // After mutations (assign table, save edits), the context's reservations
  // array has the fresh values, but the snapshot is stale — so dropdowns
  // and fields would render the old data. Look the row up live each render,
  // and fall back to the snapshot only if the row has been deleted.
  const resv = useMemo<Reservation | null>(() => {
    if (!snapshot) return null;
    return reservations.find((r) => r.id === snapshot.id) ?? snapshot;
  }, [snapshot, reservations]);

  const open = !!snapshot;
  useFocusTrap(drawerRef, open, onClose);

  // Reset the form whenever we enter edit mode for a fresh booking.
  useEffect(() => {
    if (resv && editing) {
      setForm({
        name: resv.name,
        phone: resv.phone ?? '',
        covers: resv.covers,
        start: resv.start,
        service: resv.service,
        noteIt: resv.note.it,
      });
    } else if (!editing) {
      setForm(null);
    }
  }, [editing, resv]);

  // Always exit edit mode when the drawer closes.
  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  const cust = resv?.cust ? customers.find((c) => c.id === resv.cust) ?? null : null;
  const svc = resv ? services.find((s) => s.id === resv.service) ?? null : null;

  // ── handlers ────────────────────────────────────────────────
  const handleStatusAction = async (
    kind: 'confirm' | 'cancel' | 'seat',
    newStatus: 'conf' | 'cancel' | 'seated',
  ) => {
    if (!resv || busy) return;
    setBusy(kind);
    try {
      await actions.setReservationStatus(resv.id, newStatus);
      onClose();
    } catch (err) {
      console.error('[resv-drawer] status update failed:', err);
    } finally {
      setBusy(null);
    }
  };

  const handleTableAssign = async (tableLabel: string) => {
    if (!resv || busy) return;
    setBusy('table');
    try {
      const matched = tables.find((tb) => tb.id === tableLabel);
      // In API mode the server wants a UUID; we carry it on Table.dbId.
      // Empty string from the dropdown means "unassign" → null.
      const tableId = tableLabel === '' ? null : matched?.dbId ?? tableLabel;
      await actions.patchReservation(resv.id, { tableId });
    } catch (err) {
      console.error('[resv-drawer] table assign failed:', err);
    } finally {
      setBusy(null);
    }
  };

  const handleSaveEdits = async () => {
    if (!resv || !form || busy) return;
    setBusy('save');
    try {
      await actions.patchReservation(resv.id, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        covers: form.covers,
        // L'orario modificato resta ancorato al giorno della prenotazione
        // stessa (le fixture mock, senza startsAt, ricadono sul demo day).
        startsAt: new Date(
          `${resv.startsAt?.slice(0, 10) ?? DEMO_DAY}T${form.start}:00.000Z`,
        ).toISOString(),
        service: form.service,
        noteIt: form.noteIt.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      console.error('[resv-drawer] save failed:', err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className={'scrim' + (open ? ' open' : '')} onClick={onClose} aria-hidden="true" />
      <aside
        ref={drawerRef}
        className={'drawer' + (open ? ' open' : '')}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label={resv ? `Reservation — ${resv.name}` : 'Reservation'}
        tabIndex={-1}
      >
        {resv && (
          <>
            <div className="dr-head">
              <div className="dr-top">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="eye" style={{ marginBottom: 6 }}>
                    {svc?.name[lang]} · {svc?.time}
                  </div>
                  {editing && form ? (
                    <input
                      className="input"
                      style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 500, letterSpacing: '-0.02em', padding: '6px 10px', background: 'var(--panel-2)' }}
                      value={form.name}
                      onChange={(e) => setForm((f) => f && { ...f, name: e.target.value })}
                      autoFocus
                    />
                  ) : (
                    <div className="dr-name">{resv.name}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="dr-close"
                  onClick={onClose}
                  aria-label={t('cancel', lang)}
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="dr-chips">
                <Pill status={resv.status} lang={lang} />
                <Tags tags={resv.tags} lang={lang} />
                {!editing && (
                  <button
                    type="button"
                    className="chip"
                    style={{ marginLeft: 'auto', cursor: 'pointer', borderColor: 'var(--rule-strong)' }}
                    onClick={() => setEditing(true)}
                  >
                    <Icon name="edit" size={11} />
                    {t('edit', lang)}
                  </button>
                )}
              </div>
            </div>

            <div className="dr-body">
              {/* Stats strip — always visible, immutable in edit mode
                  (use the body inputs to change time/covers) */}
              {!editing ? (
                <div className="dr-stats">
                  <div className="dr-stat">
                    <div className="v tnum">{resv.covers}</div>
                    <div className="k">{t('covers', lang)}</div>
                  </div>
                  <div className="dr-stat">
                    <div className="v tnum">{resv.start}</div>
                    <div className="k">{t('time', lang)}</div>
                  </div>
                  <div className="dr-stat">
                    <div className="v">{resv.table || '—'}</div>
                    <div className="k">{t('table', lang)}</div>
                  </div>
                </div>
              ) : (
                form && (
                  <div className="field-row" style={{ marginBottom: 18 }}>
                    <div className="field" style={{ maxWidth: 100, marginBottom: 0 }}>
                      <label htmlFor="ed-covers">{t('covers', lang)}</label>
                      <input
                        id="ed-covers"
                        type="number"
                        min={1}
                        max={50}
                        className="input tnum"
                        value={form.covers}
                        onChange={(e) => setForm((f) => f && { ...f, covers: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })}
                      />
                    </div>
                    <div className="field" style={{ maxWidth: 130, marginBottom: 0 }}>
                      <label htmlFor="ed-time">{t('time', lang)}</label>
                      <TimeSelect
                        id="ed-time"
                        value={form.start}
                        onChange={(v) => setForm((f) => f && { ...f, start: v })}
                      />
                    </div>
                  </div>
                )
              )}

              {/* Always-visible table picker — common edit, lives outside
                  the Modifica flow so unassigned bookings can be seated
                  in one click. */}
              <div className="dr-field">
                <div className="dk">{t('table', lang)}</div>
                <div className="dv">
                  <select
                    className="input"
                    value={resv.table || ''}
                    onChange={(e) => handleTableAssign(e.target.value)}
                    disabled={busy !== null}
                    style={{ height: 32, padding: '0 10px', fontSize: 13, background: 'var(--panel-2)' }}
                  >
                    <option value="">— nessuno —</option>
                    {tables.map((tb) => (
                      <option key={tb.id} value={tb.id}>
                        {tb.id} · {tb.seats} {t('seats', lang)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="dr-field">
                <div className="dk">{t('source', lang)}</div>
                <div className="dv">{t(`src_${resv.source}`, lang)}</div>
              </div>

              {editing && form ? (
                <>
                  <div className="dr-field">
                    <div className="dk">{t('phone', lang)}</div>
                    <div className="dv">
                      <input
                        className="input tnum"
                        value={form.phone}
                        onChange={(e) => setForm((f) => f && { ...f, phone: e.target.value })}
                        placeholder="+39 …"
                        style={{ height: 30 }}
                      />
                    </div>
                  </div>
                  <div className="dr-field">
                    <div className="dk">{t('note', lang)}</div>
                    <div className="dv">
                      <textarea
                        className="textarea"
                        value={form.noteIt}
                        onChange={(e) => setForm((f) => f && { ...f, noteIt: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {resv.phone && (
                    <div className="dr-field">
                      <div className="dk">{t('phone', lang)}</div>
                      <div className="dv tnum">{resv.phone}</div>
                    </div>
                  )}
                  {resv.note[lang] && (
                    <div className="dr-field">
                      <div className="dk">{t('note', lang)}</div>
                      <div className="dv note">{resv.note[lang]}</div>
                    </div>
                  )}
                </>
              )}

              {cust && !editing && (
                <>
                  <div className="dr-section-label">{t('guest_label', lang)}</div>
                  <div className="dr-field">
                    <div className="dk">{t('history', lang)}</div>
                    <div className="dv">
                      {cust.visits} {t('visits', lang)} · {t('avg', lang)} {cust.spend}
                    </div>
                  </div>
                  {cust.fav && cust.fav !== '—' && (
                    <div className="dr-field">
                      <div className="dk">{t('favourite', lang)}</div>
                      <div className="dv">{cust.fav}</div>
                    </div>
                  )}
                  {cust.email && (
                    <div className="dr-field">
                      <div className="dk">{t('email', lang)}</div>
                      <div className="dv">{cust.email}</div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Foot: edit mode shows Save/Cancel; otherwise the usual
                status-action buttons. */}
            <div className="dr-foot">
              {editing ? (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setEditing(false)}
                    disabled={busy !== null}
                  >
                    {t('cancel', lang)}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveEdits}
                    disabled={busy !== null}
                  >
                    {busy === 'save' ? '…' : t('save', lang)}
                  </button>
                </>
              ) : resv.status === 'new' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-terra"
                    disabled={busy !== null}
                    onClick={() => handleStatusAction('confirm', 'conf')}
                  >
                    <Icon name="check" size={15} />
                    {busy === 'confirm' ? '…' : t('confirm', lang)}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy !== null}
                    onClick={() => handleStatusAction('cancel', 'cancel')}
                  >
                    <Icon name="x" size={15} />
                    {busy === 'cancel' ? '…' : t('cancel', lang)}
                  </button>
                </>
              ) : resv.status === 'conf' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy !== null}
                    onClick={() => handleStatusAction('seat', 'seated')}
                  >
                    <Icon name="check" size={15} />
                    {busy === 'seat' ? '…' : t('seat', lang)}
                  </button>
                  {/* WhatsApp a un tocco: conferma precompilata, gratis —
                      parte dal numero del ristoratore, non da un'API. */}
                  {resv.phone && (
                    <a
                      className="btn btn-ghost"
                      href={waLink(resv.phone, waBookingMessage(resv, tenant.name, 'conf'))}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon name="message" size={15} />
                      WhatsApp
                    </a>
                  )}
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-ghost" disabled>
                    <Icon name="check" size={15} />
                    {t('seat', lang)}
                  </button>
                  {resv.status === 'cancel' && resv.phone && (
                    <a
                      className="btn btn-ghost"
                      href={waLink(resv.phone, waBookingMessage(resv, tenant.name, 'cancel'))}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon name="message" size={15} />
                      WhatsApp
                    </a>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
